import { ReactNode, RefObject, useRef, useState } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { twJoin, twMerge } from "tailwind-merge";
import { useEventListener } from "usehooks-ts";

import { MotionDropdownPanel } from "src/base-components/MotionDropdownPanel";
import { HeadlessUIPlacement } from "src/utils/headlessui";

export type DropDownElement<T> = {
  key: string;
  value: T;
  disabled?: boolean;
  icon?: IconProp;
  helpText?: string;
  helpBody?: React.ReactNode;
};

export type RenderValueFn<T> = (
  element: DropDownElement<T> & { selected: boolean },
) => ReactNode;

export type DropDownProps<T> = {
  elements: DropDownElement<T>[];
  renderButtonValue: (value?: T) => ReactNode;
  renderValue: RenderValueFn<T>;
  renderFooter?: () => ReactNode;
  renderHeader?: () => ReactNode;
  onSelect: (key: string) => void;
  selectedKey?: string;
  className?: string;
  buttonClassName?: string;
  placement?: "topRight" | "topLeft" | "bottomRight" | "bottomLeft" | "bottom";
  buttonDataLoc?: string;
  itemsClassNames?: string;
  disabled?: boolean;
  variant?: "light" | "transparent";
  dataLoc?: string;
};

const translatePlacement = (
  placement: "topRight" | "topLeft" | "bottomRight" | "bottomLeft" | "bottom",
): HeadlessUIPlacement => {
  switch (placement) {
    case "topRight":
      return "top end";
    case "topLeft":
      return "top start";
    case "bottomRight":
      return "bottom end";
    case "bottomLeft":
      return "bottom start";
    case "bottom":
      return "bottom";
  }
};

// HeadlessUI have a bug with --button-width css variable
// https://github.com/tailwindlabs/headlessui/issues/3598
// This is a workaround to set the width of the dropdown items to the width of the button
const useCustomDropdownWidth = (ref: RefObject<HTMLDivElement>) => {
  const [buttonWidth, setButtonWidth] = useState(0);

  useEventListener(
    "transitionend",
    (e) => {
      if (
        e.propertyName === "transform" &&
        e.target instanceof HTMLElement &&
        ref.current &&
        e.target.id.includes("dialog-panel") &&
        e.target.contains(ref.current)
      ) {
        // Set custom width if element is in Modal and right after transitionend
        setButtonWidth(ref.current.getBoundingClientRect().width);
      }
    },
    undefined,
    { passive: true },
  );

  return buttonWidth > 0
    ? `min-w-[${buttonWidth}px]`
    : "min-w-[var(--button-width)]";
};

/*
 * @deprecated please use FixedSimpleDropdown or FixedPositionedDropdown instead
 */
export const DropDown = <T,>({
  disabled = false,
  onSelect,
  elements,
  renderButtonValue,
  renderValue,
  renderFooter,
  selectedKey,
  className,
  buttonClassName,
  placement = "bottomRight",
  buttonDataLoc,
  itemsClassNames,
  renderHeader,
  variant = "light",
  dataLoc,
}: DropDownProps<T>) => {
  const selected = elements.find((element) => element.key === selectedKey);

  const ref = useRef<HTMLDivElement>(null);
  const itemsWidth = useCustomDropdownWidth(ref);

  const renderItems = () =>
    elements.map((element: DropDownElement<T>) => (
      <MenuItem key={element.key} disabled={element.disabled}>
        <span
          className="block min-h-8 cursor-pointer ui-active:bg-gray-50 font-inter-normal-13px text-gray-800"
          onClick={() => onSelect(element.key)}
        >
          {renderValue({
            ...element,
            selected: selected?.key === element.key,
          })}
        </span>
      </MenuItem>
    ));

  const renderFooterAsItem = () =>
    renderFooter ? (
      <MenuItem>
        <span className="block cursor-pointer ui-active:bg-gray-50 font-inter-normal-12px text-gray-800">
          {renderFooter()}
        </span>
      </MenuItem>
    ) : undefined;

  return (
    <Menu
      ref={ref}
      as="div"
      className={twJoin(
        "text-left font-inter-normal-13px text-gray-800",
        className,
      )}
      data-loc={dataLoc}
    >
      {({ open }) => (
        <>
          <MenuButton
            className={twMerge(
              "h-full w-full justify-center rounded-lg font-inter-normal-12px",
              "focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-600/25",
              selected?.value && "text-gray-500",
              elements.length === 0 && "cursor-default",
              variant === "light" &&
                !disabled &&
                "border border-gray-200 bg-white",
              variant === "light" &&
                disabled &&
                "border border-gray-200 bg-gray-100",
              open &&
                "border-indigo-500 outline-none ring-2 ring-indigo-600/25",
              buttonClassName,
            )}
            data-loc={buttonDataLoc}
            disabled={disabled || elements.length === 0}
          >
            {renderButtonValue(selected?.value)}
          </MenuButton>
          <MenuItems
            anchor={{
              to: translatePlacement(placement),
              gap: 6,
            }}
            as={MotionDropdownPanel}
            className={twMerge(itemsClassNames, itemsWidth)}
            data-loc={`${buttonDataLoc ?? dataLoc}-dropdown`}
          >
            {renderHeader?.()}
            {renderItems()}
            {renderFooterAsItem()}
          </MenuItems>
        </>
      )}
    </Menu>
  );
};
